import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Coordinates} from "sigma/types";
import {useOverlayContext} from "../../contexts/OverlayContext";

import {useFileDataset, useFileRegions} from "../../hooks/useJsonLoader";
import {Bounds, Region} from "../../types/Types";
import {buildSmoothQuadraticCurvePath} from "../../utils/draw";
import {getDatasetBounds} from "../../utils/dataset";
import {drawOverlayAll, makeCoordinateMapper} from "../../utils/drawOverlay";

const OverlayCanvas = () => {
  const {overlayResults, hoveredRegions, setHoveredRegions} = useOverlayContext();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const shapesRef = useRef<{ region: Region; path: Path2D }[]>([]);

  const dataset = useFileDataset();
  const regions = useFileRegions();

  const [mousePos, setMousePos] = useState({x: 0, y: 0});
  const [hoveredEnrichedRegions, setHoveredEnrichedRegions] = useState<Array<Region>>([]);

  /**
   * Coordinates of the network bounds, calculated from the dataset.
   */
  const bounds: Bounds | null = useMemo(() => getDatasetBounds(dataset), [dataset]);

  /**
   * Mapping gene label to Node data, to lookup node coordinates.
   */
  const geneToNode = useMemo(
    () =>
      Object.fromEntries(
        dataset?.nodes.map(node => [node.n, node]) ?? []
      ),
    [dataset?.nodes]);

  /**
   * Mapping region name to Region data, to lookup region coordinates.
   */
  const regionLookup = useMemo(
    () =>
      Object.fromEntries(
        regions?.map(region => [region.name, region]) ?? []
      ),
    [regions]);

  /**
   * Draw routine for all elements on the canvas.
   */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bounds) return;

    drawOverlayAll(
      canvas,
      bounds,
      regions,
      hoveredRegions,
      overlayResults,
      regionLookup,
      geneToNode
    );
  }, [overlayResults, dataset, regions, hoveredRegions, bounds, regionLookup, geneToNode]);

  /**
   * Redraw on data change, triggering dependecies are in draw callback above
   */
  useEffect(() => {
    draw();
  }, [draw]);

  // Resize observer: sync buffer size with rendered size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    const parent = document.getElementById("page-content");
    if (!parent) return;

    let lastTime = 0;
    const frameInterval = 1000 / 30; // throttle to ~30fps

    const resize = () => {
      const parentWidth = parent.offsetWidth;
      const parentHeight = parent.offsetHeight;

      const dpr = window.devicePixelRatio || 1;

      canvas.style.width = `${parentWidth}px`;
      canvas.style.height = `${parentHeight}px`;

      canvas.width = parentWidth * dpr;
      canvas.height = parentHeight * dpr;

      const ctx = canvas.getContext("2d");
      if (ctx && dpr !== 1) ctx.scale(dpr, dpr);
      draw();

      if (!bounds) return;

      const mapToCanvas = makeCoordinateMapper(bounds, canvas);

      shapesRef.current = [];

      regions?.forEach(region => {
        const vertices = region.outline.map((v) =>
          mapToCanvas(v as Coordinates)
        );

        const path = new Path2D();
        buildSmoothQuadraticCurvePath(path, vertices);

        shapesRef.current.push({region, path});
      });
    };

    const observer = new ResizeObserver(() => {
      const now = performance.now();
      if (now - lastTime > frameInterval) {
        lastTime = now;
        requestAnimationFrame(resize);
      }
    });

    observer.observe(parent);
    resize(); // initial sync

    const handleMouseMove = (event: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const x = (event.clientX - rect.left) * dpr;
      const y = (event.clientY - rect.top) * dpr;
      const hovered: Array<Region> = [];

      for (const {region, path} of shapesRef.current) {
        if (ctx?.isPointInPath(path, x, y)) {
          hovered.push(region);
        }
      }

      /** Only update if changed */
      if (hovered.length !== hoveredRegions.length ||
        hovered.some((r, i) => r !== hoveredRegions[i])) {
        setHoveredRegions(hovered);
        setHoveredEnrichedRegions(
          hovered.filter(r => r.name in (overlayResults?.enrichment.enriched_domains ?? {})));
      }

      setMousePos({
        x: event.clientX,
        y: event.clientY,
      });
    };

    canvas.addEventListener("mousemove", handleMouseMove);

    return () => {
      observer.disconnect();
      canvas.removeEventListener("mousemove", handleMouseMove);
    };
  }, [draw]);

  return <>
    <canvas ref={canvasRef} className="d-block position-absolute"/>
    {hoveredEnrichedRegions.length > 0 && (
      <div id="overlay-tooltip" style={{top: mousePos.y, left: mousePos.x + 10}}>
        {hoveredEnrichedRegions.map(region => <div key={region.name}>
          {region.name}<br/>
        </div>)}
      </div>
    )}
  </>
};

export default OverlayCanvas;